import {
    UNIT_TYPES,
    createId,
    nowIso,
    getState,
    mutate,
    findUnit,
    enrichUnit,
    delay,
} from './store';
import organizationalUnitsMock from './organizationalUnits';

function asRole(unit, snapshot = getState()) {
    if (!unit || unit.type !== UNIT_TYPES.ROLE) return null;
    const sector = unit.parentId ? findUnit(unit.parentId, snapshot) : null;
    const board = sector?.parentId ? findUnit(sector.parentId, snapshot) : null;
    const users = snapshot.users.filter((u) => u.roleId === unit.id);
    return {
        ...enrichUnit(unit, snapshot),
        sectorId: sector?.id || null,
        sectorName: sector?.name || null,
        boardId: board?.id || null,
        boardName: board?.name || null,
        courseIds: unit.courseIds || [],
        collaborators: users.map((u) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            status: u.status,
        })),
        collaboratorsCount: users.length,
    };
}

export default {
    async list({ sectorId, status, search } = {}) {
        await delay();
        const snapshot = getState();
        let roles = (snapshot.units || [])
            .filter((u) => u.type === UNIT_TYPES.ROLE)
            .map((u) => asRole(u, snapshot))
            .filter(Boolean);

        if (sectorId) roles = roles.filter((r) => r.sectorId === sectorId);
        if (status) roles = roles.filter((r) => r.status === status);
        if (search) {
            const term = search.toLowerCase();
            roles = roles.filter(
                (r) => r.name.toLowerCase().includes(term) || (r.description || '').toLowerCase().includes(term),
            );
        }

        return {
            content: roles.sort((a, b) => a.order - b.order),
            totalElements: roles.length,
            page: 0,
            size: roles.length,
        };
    },

    async getById(id) {
        await delay();
        const role = asRole(findUnit(id));
        if (!role) {
            const error = new Error('Cargo não encontrado.');
            error.code = 'NOT_FOUND';
            throw error;
        }
        return role;
    },

    async create(payload) {
        await delay();
        const created = await organizationalUnitsMock.create({
            type: UNIT_TYPES.ROLE,
            parentId: payload.sectorId || payload.parentId,
            name: payload.name,
            description: payload.description,
            managerUserId: payload.managerUserId,
            status: payload.status || 'active',
            courseIds: payload.courseIds || [],
        });
        return asRole(findUnit(created.id));
    },

    async update(id, payload) {
        await delay();
        const updates = {
            name: payload.name,
            description: payload.description,
            managerUserId: payload.managerUserId,
            status: payload.status,
            courseIds: payload.courseIds,
        };
        if (payload.sectorId || payload.parentId) {
            updates.parentId = payload.sectorId || payload.parentId;
        }
        Object.keys(updates).forEach((key) => {
            if (updates[key] === undefined) delete updates[key];
        });
        await organizationalUnitsMock.update(id, updates);
        return asRole(findUnit(id));
    },

    async remove(id) {
        return organizationalUnitsMock.remove(id);
    },

    async duplicate(id) {
        await delay();
        return mutate((draft) => {
            const source = draft.units.find((u) => u.id === id && u.type === UNIT_TYPES.ROLE);
            if (!source) {
                const error = new Error('Cargo não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            const copy = {
                ...JSON.parse(JSON.stringify(source)),
                id: createId('unit'),
                name: `${source.name} (cópia)`,
                courseIds: [...(source.courseIds || [])],
                createdAt: nowIso(),
                updatedAt: nowIso(),
            };
            draft.units.push(copy);

            const relatedAssignments = draft.courseAssignments.filter(
                (a) => a.roleId === id || a.organizationalUnitId === id,
            );
            relatedAssignments.forEach((assignment) => {
                draft.courseAssignments.push({
                    ...assignment,
                    id: createId('assign'),
                    roleId: copy.id,
                    organizationalUnitId: copy.id,
                    createdAt: nowIso(),
                    updatedAt: nowIso(),
                });
            });

            return asRole(copy, draft);
        });
    },
};
